/**
 * @class
 * @function FacetToggle
 */
class FacetToggle extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.container = this.closest('.facets--bar-sticky');

    this.addEventListener('click', (event) => {
      event.preventDefault();

      /*
       * Look for the drawer when the button is clicked.
       * This allows dynamically inserted Best Sellers
       * facet drawers to work.
       */
      const drawer = document.getElementById('Facet-Drawer');

      if (!drawer) return;

      document.body.classList.toggle('open-cc');
      drawer.classList.toggle('active');
    });

    if (this.container) {
      this.enableSticky();
    }
  }

  enableSticky() {
    const headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height'),
      10
    );

    const rootMargin =
      (Number.isNaN(headerHeight) ? 0 : headerHeight) + 1;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        entry.target.classList.toggle(
          'is-pinned',
          entry.intersectionRatio <= 1 &&
            entry.intersectionRatio > 0
        );
      },
      {
        threshold: [1],
        rootMargin: `-${rootMargin}px 0px 0px 0px`
      }
    );

    this.observer.observe(this.container);
  }
}

customElements.define('facet-toggle', FacetToggle);

/**
 * @class
 * @function FacetFiltersForm
 */





class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick =
      this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (event.target.id === 'compare_toggle') {
        return;
      }

      this.onSubmitHandler(event);
    }, 250);

    const form = this.querySelector('form');

    if (form) {
      form.addEventListener(
        'input',
        (event) => {
          /*
           * Compare toggle isn't a facet request.
           */
          if (
            event.target.id ===
            'compare_toggle'
          ) {
            return;
          }


          /*
           * The user has made a newer
           * filter selection.
           *
           * Immediately stop the older
           * request BEFORE the debounce
           * finishes.
           */
          if (
            FacetFiltersForm.abortController
          ) {
            FacetFiltersForm
              .abortController
              .abort();

            FacetFiltersForm
              .abortController = null;
          }

          const target = event.target;

          if (
            target.matches(
              'input[type="checkbox"], input[type="radio"]'
            ) &&
            !target.closest('price-slider')
          ) {
            /*
             * Normal facets can update immediately.
             *
             * Examples:
             * Brand
             * Product Category
             * Filter Type
             * Bottom Type
             * Availability
             */
            this.onSubmitHandler(event);
          } else {
            /*
             * Price controls stay debounced so the
             * slider can finish synchronizing its
             * Price Range metafield checkboxes.
             */
            this.debouncedOnSubmit(event);
          }

        }
      );
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (
        searchParams ===
        FacetFiltersForm.searchParamsPrev
      ) {
        return;
      }

      /*
       * Popstate applies to normal search and
       * collection facets.
       *
       * Search-page Best Sellers fallback does not
       * change the URL, while empty-collection
       * fallback filters do.
       */
      FacetFiltersForm.renderPage(
        searchParams,
        null,
        false
      );
    };

    window.addEventListener(
      'popstate',
      onHistoryChange
    );
  }

  static toggleActiveFacets(
    disable = true,
    scope = document
  ) {
    scope
      .querySelectorAll('.js-facet-remove')
      .forEach((element) => {
        element.classList.toggle(
          'disabled',
          disable
        );
      });
  }

  /**
   * Normal search and collection filtering.
   */
  static renderPage(
    searchParams,
    event,
    updateURLHash = true
  ) {
    if (
      FacetFiltersForm.abortController
    ) {
      FacetFiltersForm.abortController.abort();
    }

    FacetFiltersForm.abortController =
      new AbortController();

    const requestSignal =
      FacetFiltersForm.abortController.signal;

    FacetFiltersForm.searchParamsPrev =
      searchParams;

    const sections =
      FacetFiltersForm.getSections();

    const countContainers =
      document.getElementsByClassName(
        'thb-filter-count'
      );

    const productGridContainer =
      document.getElementById(
        'ProductGridContainer'
      );

    const collection =
      productGridContainer?.querySelector(
        '.collection'
      );

    if (collection) {
      collection.classList.add('loading');
    }

    for (const item of countContainers) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const query = searchParams
        ? `&${searchParams}`
        : '';

      const url =
        `${window.location.pathname}` +
        `?section_id=${section.section}` +
        query;

      const filterDataUrl = (element) =>
        element.url === url;

      if (
        FacetFiltersForm.filterData.some(
          filterDataUrl
        )
      ) {
        FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event
        );
      } else {
        FacetFiltersForm.renderSectionFromFetch(
          url,
          event,
          requestSignal
        );
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(
        searchParams
      );
    }
  }

  static renderSectionFromFetch(
    url,
    event,
    signal
  ) {
    fetch(url, {
      signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            'Unable to load filtered results.'
          );
        }

        return response.text();
      })
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url
          }
        ];

        FacetFiltersForm.renderFilters(
          html,
          event
        );

        FacetFiltersForm.renderProductGridContainer(
          html
        );

        FacetFiltersForm.renderProductCount(
          html
        );

        FacetFiltersForm.notifyFacetUpdate();
      })
      .catch((error) => {
        /*
         * Ignore requests that were intentionally
         * cancelled because the user selected
         * another filter.
         */
        if (error.name === 'AbortError') {
          return;
        }

        console.error(
          'Facet filtering failed:',
          error
        );
      });
  }

  static renderSectionFromCache(
    filterDataUrl,
    event
  ) {
    const cachedData =
      FacetFiltersForm.filterData.find(
        filterDataUrl
      );

    if (!cachedData) return;

    const html = cachedData.html;

    FacetFiltersForm.renderFilters(
      html,
      event
    );

    FacetFiltersForm.renderProductGridContainer(
      html
    );

    FacetFiltersForm.renderProductCount(
      html
    );

    FacetFiltersForm.notifyFacetUpdate();
  }

  static renderProductGridContainer(html) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const source =
      parsedHTML.getElementById(
        'ProductGridContainer'
      );

    const target =
      document.getElementById(
        'ProductGridContainer'
      );

    if (!source || !target) return;

    const sourceGrid =
      source.querySelector('#product-grid');

    const isCollectionZeroResults =
      sourceGrid &&
      sourceGrid.classList.contains(
        'collection--empty'
      );

    const sourceResultsContent =
      source.querySelector(
        '[data-collection-results-content]'
      );

    const targetResultsContent =
      target.querySelector(
        '[data-collection-results-content]'
      );

    /*
     * COLLECTION ZERO-RESULT STATE
     *
     * Keep the existing sidebar DOM completely
     * untouched. Only refresh the right side.
     *
     * This preserves:
     * - checkbox state
     * - price slider instance
     * - event listeners
     * - original facet values
     */
    if (
      isCollectionZeroResults &&
      sourceResultsContent &&
      targetResultsContent
    ) {
      targetResultsContent.innerHTML =
        sourceResultsContent.innerHTML;

      const sourceSidebar =
        source.querySelector(
          '.sidebar-container'
        );

      const targetSidebar =
        target.querySelector(
          '.sidebar-container'
        );

      if (
        sourceSidebar &&
        targetSidebar
      ) {
        const resultClass =
          Array.from(
            sourceSidebar.classList
          ).find((className) =>
            className.startsWith(
              'results--'
            )
          );

        Array.from(
          targetSidebar.classList
        )
          .filter((className) =>
            className.startsWith(
              'results--'
            )
          )
          .forEach((className) => {
            targetSidebar.classList.remove(
              className
            );
          });

        if (resultClass) {
          targetSidebar.classList.add(
            resultClass
          );
        }
      }

      return;
    }

    /*
     * Normal results:
     * keep existing behavior.
     */
    target.innerHTML =
      source.innerHTML;
  }

  static renderProductCount(html) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const countHtml =
      parsedHTML.getElementById(
        'ProductCount'
      );

    const containers =
      document.getElementsByClassName(
        'thb-filter-count'
      );

    if (!countHtml) return;

    for (const item of containers) {
      item.innerHTML = countHtml.innerHTML;
      item.classList.remove('loading');
    }
  }

  static renderFilters(
    html,
    event,
    scope = document
  ) {
    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    /*
     * Determine whether this is a COLLECTION
     * response that has been filtered down
     * to zero products.
     */
    const sourceGrid =
      parsedHTML.querySelector(
        '#ProductGridContainer #product-grid'
      );

    const isCollectionZeroResults =
      sourceGrid &&
      sourceGrid.classList.contains(
        'collection--empty'
      );

    const isCollectionPage =
      window.location.pathname.startsWith(
        '/collections/'
      );

    const shouldPreserveCollectionFacets =
      isCollectionPage &&
      isCollectionZeroResults;

    const desktopSource =
      parsedHTML.querySelector(
        '#FacetFiltersFormDesktop .facets__wrapper'
      );

    const mobileSource =
      parsedHTML.querySelector(
        '#FacetFiltersFormMobile .facets__wrapper'
      );

    const desktopTarget =
      scope.querySelector(
        '#FacetFiltersFormDesktop .facets__wrapper'
      );

    const mobileTarget =
      scope.querySelector(
        '#FacetFiltersFormMobile .facets__wrapper'
      );

    /*
     * IMPORTANT:
     *
     * When a collection filter produces zero
     * products, DO NOT overwrite the existing
     * desktop sidebar.
     *
     * The existing sidebar already contains
     * the user's selected checkbox/price state
     * and initialized slider.
     */
    if (
      !shouldPreserveCollectionFacets &&
      desktopSource &&
      desktopTarget
    ) {
      desktopTarget.innerHTML =
        desktopSource.innerHTML;
    }

    /*
     * Keep existing mobile behavior for now.
     */
    if (
      mobileSource &&
      mobileTarget
    ) {
      mobileTarget.innerHTML =
        mobileSource.innerHTML;
    }

    FacetFiltersForm.renderActiveFacets(
      parsedHTML,
      scope
    );

    FacetFiltersForm.renderAdditionalElements(
      parsedHTML,
      scope
    );
  }

  static renderActiveFacets(
    html,
    scope = document
  ) {
    const selectors = [
      '.active-facets'
    ];

    selectors.forEach((selector) => {
      const source =
        html.querySelector(selector);

      const target =
        scope.querySelector(selector);

      if (!source || !target) return;

      target.innerHTML =
        source.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(
      false,
      scope
    );
  }

  static renderAdditionalElements(
    html,
    scope = document
  ) {
    const selectors = [
      '.mobile-filter-count'
    ];

    selectors.forEach((selector) => {
      const source =
        html.querySelector(selector);

      const target =
        scope.querySelector(selector);

      if (!source || !target) return;

      target.innerHTML =
        source.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement =
      target.querySelector(
        '.facets__selected'
      );

    const sourceElement =
      source.querySelector(
        '.facets__selected'
      );

    if (
      sourceElement &&
      targetElement
    ) {
      targetElement.outerHTML =
        sourceElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    /*
    * searchParams remains the INTERNAL Shopify
    * request. That allows facets to continue
    * filtering the mapped Clarus products.
    */
    let visibleSearchParams =
      searchParams;

    const compatState =
      window.clarusCompatibilitySearch;

    if (
      compatState?.originalQuery &&
      compatState?.internalQuery
    ) {
      const visibleParams =
        new URLSearchParams(
          searchParams
        );

      /*
      * Browser/customer sees the original query.
      */
      visibleParams.set(
        'q',
        compatState.originalQuery
      );

      visibleParams.delete(
        'compat_q'
      );

      visibleParams.delete(
        'compat_sku'
      );

      visibleSearchParams =
        visibleParams.toString();
    }

    history.pushState(
      {
        /*
        * IMPORTANT:
        *
        * Keep the INTERNAL params in history state
        * so back/forward and AJAX filtering still
        * use the mapped Clarus query.
        */
        searchParams
      },
      '',
      `${window.location.pathname}${
        visibleSearchParams
          ? `?${visibleSearchParams}`
          : ''
      }`
    );
  }

  static getSections() {
    const productGrid =
      document.getElementById(
        'product-grid'
      );

    if (!productGrid?.dataset.id) {
      return [];
    }

    return [
      {
        section: productGrid.dataset.id
      }
    ];
  }

  static notifyFacetUpdate() {
    document.dispatchEvent(
      new CustomEvent('facets:updated')
    );

    document.dispatchEvent(
      new CustomEvent('facet:rendered')
    );
  }

  /**
   * Returns the Best Sellers fallback wrapper
   * when this facet form belongs to the fallback.
   */
  getBestSellersFallback() {
    return this.closest(
      '[data-best-sellers-fallback]'
    );
  }

  /**
   * Filters the actual Best Sellers collection
   * rather than submitting the filters to /search.
   */
  renderBestSellersPage(
    searchParams,
    fallback
  ) {
    if (!fallback) return;

    const collectionUrl =
      fallback.dataset.collectionUrl;

    const targetGrid =
      fallback.querySelector(
        '#ProductGridContainer #product-grid'
      );

    if (!collectionUrl || !targetGrid) {
      return;
    }

    targetGrid.classList.add('loading');

    const requestUrl = new URL(
      collectionUrl,
      window.location.origin
    );

    const incomingParams =
      new URLSearchParams(searchParams);

    /*
     * Empty COLLECTION fallback:
     * keep the browser URL synchronized
     * with the selected Best Sellers filters.
     *
     * Search fallback behavior is left alone.
     */
    if (
      fallback.hasAttribute(
        'data-collection-empty-fallback'
      )
    ) {
      const browserUrl =
        new URL(window.location.href);

      /*
       * Remove existing filter params first.
       */
      Array.from(
        browserUrl.searchParams.keys()
      ).forEach((key) => {
        if (key.startsWith('filter.')) {
          browserUrl.searchParams.delete(key);
        }
      });

      /*
       * Copy the currently selected
       * fallback filters into the collection URL.
       */
      incomingParams.forEach(
        (value, key) => {
          if (key.startsWith('filter.')) {
            browserUrl.searchParams.append(
              key,
              value
            );
          }
        }
      );

      browserUrl.searchParams.delete('page');

      history.pushState(
        {
          searchParams:
            browserUrl.searchParams.toString()
        },
        '',
        browserUrl.toString()
      );
    }

    incomingParams.forEach(
      (value, key) => {
        requestUrl.searchParams.append(
          key,
          value
        );
      }
    );

    fetch(requestUrl.toString(), {
      headers: {
        'X-Requested-With':
          'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            'Unable to filter the Best Sellers collection.'
          );
        }

        return response.text();
      })
      .then((html) => {
        const parsedHTML =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const sourceGrid =
          parsedHTML.querySelector(
            '#ProductGridContainer #product-grid'
          ) ||
          parsedHTML.querySelector(
            '#product-grid'
          );

        /*
        * IMPORTANT:
        *
        * When the filtered Best Sellers collection
        * itself returns zero products, Shopify renders
        * our collection fallback markup inside that
        * collection--empty grid.
        *
        * We MUST NOT copy those fallback products
        * back into the current fallback grid.
        */
        const sourceGridIsEmpty =
          sourceGrid?.classList.contains(
            'collection--empty'
          );

        if (
          sourceGrid &&
          !sourceGridIsEmpty
        ) {
          targetGrid.innerHTML =
            sourceGrid.innerHTML;
        } else {
          targetGrid.innerHTML = '';
        }

        /*
        * Keep the grid's empty state synchronized
        * with the actual Best Sellers response.
        */
        targetGrid.classList.toggle(
          'collection--empty',
          Boolean(sourceGridIsEmpty)
        );

        this.renderBestSellersFilters(
          parsedHTML,
          fallback,
          collectionUrl
        );

        targetGrid.classList.remove(
          'loading'
        );

        FacetFiltersForm.notifyFacetUpdate();
      })
      .catch((error) => {
        targetGrid.classList.remove(
          'loading'
        );

        console.error(
          'Best Sellers filtering failed:',
          error
        );
      });
  }

  /**
   * Updates the desktop and mobile filter options
   * using the filtered collection response.
   */
  renderBestSellersFilters(
    parsedHTML,
    fallback,
    collectionUrl
  ) {

    const desktopSource =
      parsedHTML.querySelector(
        '#FacetFiltersFormDesktop .facets__wrapper'
      );

    const mobileSource =
      parsedHTML.querySelector(
        '#FacetFiltersFormMobile .facets__wrapper'
      );

    const desktopTarget =
      fallback.querySelector(
        '#FacetFiltersFormDesktop .facets__wrapper'
      );

    const mobileTarget =
      fallback.querySelector(
        '#FacetFiltersFormMobile .facets__wrapper'
      );

    if (
      desktopSource &&
      desktopTarget
    ) {
      desktopTarget.innerHTML =
        desktopSource.innerHTML;
    }

    if (
      mobileSource &&
      mobileTarget
    ) {
      mobileTarget.innerHTML =
        mobileSource.innerHTML;
    }

    const desktopForm =
      fallback.querySelector(
        '#FacetFiltersFormDesktop'
      );

    const mobileForm =
      fallback.querySelector(
        '#FacetFiltersFormMobile'
      );

    if (desktopForm) {
      desktopForm.action =
        collectionUrl;
    }

    if (mobileForm) {
      mobileForm.action =
        collectionUrl;
    }

    console.log(
      'Best Sellers active facets:',
      parsedHTML.querySelectorAll(
        '.active-facets'
      )
    );

    console.log(
      'Best Sellers active buttons:',
      parsedHTML.querySelectorAll(
        '.active-facets__button'
      )
    );

    const activeFacetSources =
      Array.from(
        parsedHTML.querySelectorAll(
          '.active-facets'
        )
      );

    const activeFacetsSource =
      activeFacetSources.find((container) => {
        return container.querySelector(
          '.active-facets__button, .js-facet-remove'
        );
      });

    const activeFacetsTarget =
      fallback.querySelector(
        '[data-best-sellers-active-facets]'
      );

    if (activeFacetsTarget) {
      activeFacetsTarget.innerHTML =
        activeFacetsSource
          ? activeFacetsSource.outerHTML
          : '';
    }

    const mobileCountSource =
      parsedHTML.querySelector(
        '.mobile-filter-count'
      );

    const mobileCountTarget =
      fallback.querySelector(
        '.mobile-filter-count'
      );

    if (
      mobileCountSource &&
      mobileCountTarget
    ) {
      mobileCountTarget.innerHTML =
        mobileCountSource.innerHTML;
    }

    fallback
      .querySelectorAll(
        '.mobile-filters-clear'
      )
      .forEach((clearLink) => {
        clearLink.href =
          collectionUrl;
      });

    FacetFiltersForm.toggleActiveFacets(
      false,
      fallback
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const form =
      event.target.closest('form');

    if (!form) return;

    const formData =
      new FormData(form);

    const searchParams =
      new URLSearchParams(formData);

    /*
     * PRICE RANGE FILTERING
     *
     * When our metafield price buckets are present,
     * they are the source of truth for filtering.
     *
     * The noUiSlider is only a visual/controller UI,
     * so never submit Shopify's native continuous
     * filter.v.price.gte / filter.v.price.lte values
     * at the same time.
     */
    const hasPriceRangeBuckets =
      form.querySelector(
        '[data-price-range-filter]'
      );

    if (hasPriceRangeBuckets) {
      searchParams.delete(
        'filter.v.price.gte'
      );

      searchParams.delete(
        'filter.v.price.lte'
      );
    } else {
      /*
       * Fallback to the original native Shopify
       * price behavior if this form does not contain
       * our Price Range metafield buckets.
       */
      const priceMins =
        searchParams
          .getAll('filter.v.price.gte')
          .filter((value) => {
            return value !== null &&
              value.trim() !== '';
          })
          .map(Number)
          .filter((value) => {
            return !Number.isNaN(value);
          });

      const priceMaxes =
        searchParams
          .getAll('filter.v.price.lte')
          .filter((value) => {
            return value !== null &&
              value.trim() !== '';
          })
          .map(Number)
          .filter((value) => {
            return !Number.isNaN(value);
          });

      searchParams.delete(
        'filter.v.price.gte'
      );

      searchParams.delete(
        'filter.v.price.lte'
      );

      if (priceMins.length) {
        const priceMin =
          Math.min(...priceMins);

        if (priceMin > 0) {
          searchParams.set(
            'filter.v.price.gte',
            priceMin.toFixed(2)
          );
        }
      }

      if (priceMaxes.length) {
        const priceMax =
          Math.max(...priceMaxes);

        searchParams.set(
          'filter.v.price.lte',
          priceMax.toFixed(2)
        );
      }

      if (
        searchParams.get(
          'filter.v.price.gte'
        ) === '0.00'
      ) {
        searchParams.delete(
          'filter.v.price.gte'
        );
      }

      const priceSlider =
        form.querySelector(
          '.price_slider'
        );

      if (priceSlider) {
        const maximumPrice =
          parseFloat(
            priceSlider.dataset.max
          ).toFixed(2);

        if (
          searchParams.get(
            'filter.v.price.lte'
          ) === maximumPrice
        ) {
          searchParams.delete(
            'filter.v.price.lte'
          );
        }
      }
    }

    const fallback =
      this.getBestSellersFallback();

    if (fallback) {
      this.renderBestSellersPage(
        searchParams.toString(),
        fallback
      );

      return;
    }

    FacetFiltersForm.renderPage(
      searchParams.toString(),
      event
    );
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    const fallback =
      this.getBestSellersFallback();

    FacetFiltersForm.toggleActiveFacets(
      true,
      fallback || document
    );

    const href =
      event.currentTarget.href;

    const queryIndex =
      href.indexOf('?');

    const searchParams =
      queryIndex === -1
        ? ''
        : href.slice(queryIndex + 1);

    if (fallback) {
      this.renderBestSellersPage(
        searchParams,
        fallback
      );

      return;
    }

    FacetFiltersForm.renderPage(
      searchParams
    );
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.abortController = null;

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  'facet-filters-form',
  FacetFiltersForm
);

FacetFiltersForm.setListeners();

document.addEventListener(
  'facets:updated',
  () => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const sortValue =
      params.get('sort_by') || 'manual';

    syncCollectionSortControls(
      sortValue
    );

  }
);


/**
 * Keeps collection desktop and mobile sorting in the same state.
 */
function getCollectionSortLabel(sortValue) {
  const labels = {
    manual: 'Featured',
    'best-selling': 'Recommended',
    'created-descending': 'Newest',
    'price-ascending': 'Price (Low to High)',
    'price-descending': 'Price (High to Low)',
    'title-ascending': 'Ascending (A-Z)',
    'title-descending': 'Descending (Z-A)'
  };

  return labels[sortValue] || 'Featured';
}

function syncCollectionSortControls(sortValue) {
  const label = getCollectionSortLabel(sortValue);

  const desktopInput =
    document.querySelector('[data-desktop-sort-input]');

  if (desktopInput) {
    desktopInput.value = sortValue;
  }

  document
    .querySelectorAll(
      '[data-mobile-sort-modal]:not([data-search-type="product"]):not([data-search-type="article"])'
    )
    .forEach((modal) => {
      modal
        .querySelectorAll('input[type="radio"]')
        .forEach((radio) => {
          radio.checked = radio.value === sortValue;
        });

      const current =
        modal.querySelector('[data-sort-current]');

      if (current) {
        current.textContent = label;
      }

      const trigger = document.querySelector(
        `[data-sort-open="${CSS.escape(modal.id)}"]`
      );

      const triggerValue = trigger?.querySelector(
        '[data-sort-trigger-value]'
      );

      if (triggerValue) {
        triggerValue.textContent = label;
      }
    });

  const desktopText =
    document.querySelector(
      '#FacetFiltersForm-bar .custom-select--text'
    );

  if (desktopText) {
    desktopText.textContent = ` ${label}`;
  }

  document
    .querySelectorAll(
      '#FacetFiltersForm-bar .custom-select--item button'
    )
    .forEach((button) => {
      const isActive =
        button.dataset.value === sortValue;

      button.classList.toggle(
        'custom-select--item-active',
        isActive
      );

      if (isActive) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
}

function applyCollectionSort(sortValue, event = null) {
  const url = new URL(window.location.href);

  // Preserve all active filter parameters.
  url.searchParams.set('sort_by', sortValue);

  // Return to page one when sorting changes.
  url.searchParams.delete('page');

  syncCollectionSortControls(sortValue);

  FacetFiltersForm.renderPage(
    url.searchParams.toString(),
    event
  );
}

document.addEventListener('click', (event) => {
  const applyButton =
    event.target.closest('[data-sort-apply]');

  if (!applyButton) return;

  const modalId =
    applyButton.dataset.sortApply;

  const modal =
    document.getElementById(modalId);

  if (!modal) return;

  /*
   * Search modals use clarus_sort and their existing
   * search-specific sorting logic.
   */
  if (modal.dataset.searchType) return;

  const selected =
    modal.querySelector(
      'input[type="radio"]:checked'
    );

  if (!selected) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  applyCollectionSort(
    selected.value,
    event
  );

  modal.classList.remove('is-open');
  modal.hidden = true;
  modal.setAttribute('aria-hidden', 'true');

  document.body.classList.remove(
    'overflow-hidden'
  );

  const trigger = document.querySelector(
    `[data-sort-open="${CSS.escape(modal.id)}"]`
  );

  if (trigger) {
    trigger.setAttribute(
      'aria-expanded',
      'false'
    );
  }
});


document.addEventListener(
  'DOMContentLoaded',
  () => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const sortValue =
      params.get('sort_by') || 'manual';

    syncCollectionSortControls(sortValue);

  }
);

/**
 * @class
 * @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('a').forEach(
      (item) => {
        item.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            const form =
              this.closest(
                'facet-filters-form'
              ) ||
              document.querySelector(
                'facet-filters-form'
              );

            if (!form) return;

            form.onActiveFilterClick(
              event
            );
          }
        );
      }
    );
  }
}

customElements.define(
  'facet-remove',
  FacetRemove
);

/**
 * @class
 * @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.compareToggle =
      this.querySelector(
        '#compare_toggle'
      );

    if (this.compareToggle) {
      this.compareToggle.addEventListener(
        'change',
        this.onCompare
      );
    }
  }

  onCompare(event) {
    document.body.classList.toggle(
      'compare-true'
    );

    event.preventDefault();

    return false;
  }
}

customElements.define(
  'facet-toolbar',
  FacetToolbar
);

/**
 * @class
 * @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const slider =
      this.querySelector(
        '.price_slider'
      );

    const amounts =
      this.querySelector(
        '.price_slider_amount'
      );

    if (
      !slider ||
      !amounts ||
      typeof noUiSlider === 'undefined'
    ) {
      return;
    }

    const maximum =
      parseFloat(
        slider.dataset.max
      );

    if (Number.isNaN(maximum)) {
      return;
    }

    const minimumInput =
      amounts.querySelector(
        '.field__input_min'
      );

    const maximumInput =
      amounts.querySelector(
        '.field__input_max'
      );

    const endpointLabels =
      this.querySelectorAll(
        '.endpoint_labels span'
      );

    /*
     * The Price Range metafield checkboxes are rendered
     * in the same PRICE accordion as this slider, but
     * outside the <price-slider> element itself.
     */
    const priceFilterRoot =
      this.closest('.thb-filter') ||
      this.parentElement;

    const priceRanges =
      priceFilterRoot
        ? Array.from(
            priceFilterRoot.querySelectorAll(
              '[data-price-range-filter]'
            )
          )
        : [];

    const getRangeValues = (checkbox) => {
      let min =
        parseFloat(
          checkbox.dataset.priceMin
        );

      const rawMax =
        checkbox.dataset.priceMax;

      let parsedMax =
        rawMax === undefined ||
        rawMax === null ||
        rawMax.trim() === ''
          ? NaN
          : parseFloat(rawMax);

      /*
       * Fallback:
       * If the metaobject min/max data attributes
       * are missing, derive the bucket from the
       * visible Price Range label.
       *
       * Examples:
       * Less than $25   -> 0 - 24.99
       * $25 - $49.99   -> 25 - 49.99
       * $250+          -> 250 - slider maximum
       */
      if (
        Number.isNaN(min) ||
        Number.isNaN(parsedMax)
      ) {
        const label =
          (
            checkbox.dataset.priceLabel ||
            checkbox
              .closest('li')
              ?.querySelector('.filter-name')
              ?.textContent ||
            ''
          )
            .replace(/,/g, '')
            .trim();

        const numbers =
          label.match(/\d+(?:\.\d+)?/g) || [];

        if (
          label.toLowerCase().includes('less than') &&
          numbers.length
        ) {
          const upper =
            parseFloat(numbers[0]);

          min = 0;
          parsedMax =
            Number.isNaN(upper)
              ? NaN
              : Math.max(0, upper - 0.01);
        } else if (
          label.includes('+') &&
          numbers.length
        ) {
          min =
            parseFloat(numbers[0]);

          parsedMax =
            maximum;
        } else if (
          numbers.length >= 2
        ) {
          min =
            parseFloat(numbers[0]);

          parsedMax =
            parseFloat(numbers[1]);
        }
      }

      if (
        Number.isNaN(min) ||
        Number.isNaN(parsedMax)
      ) {
        return null;
      }

      return {
        min,
        max: Math.min(parsedMax, maximum)
      };
    };

    const updateEndpointLabels = (min, max) => {
      if (endpointLabels.length < 2) {
        return;
      }

      const parsedMin =
        parseFloat(min);

      const parsedMax =
        parseFloat(max);

      endpointLabels[0].textContent =
        `$${Math.floor(
          Number.isNaN(parsedMin)
            ? 0
            : parsedMin
        )}`;

      endpointLabels[1].textContent =
        `$${Math.floor(
          Number.isNaN(parsedMax)
            ? maximum
            : parsedMax
        )}`;
    };

    /*
     * Keep the old native inputs synchronized for UI
     * compatibility, but onSubmitHandler removes their
     * params whenever our metafield buckets are present.
     */
    const updateNativePriceInputs = (min, max) => {
      if (minimumInput) {
        minimumInput.value =
          parseFloat(min).toFixed(2);
      }

      if (maximumInput) {
        maximumInput.value =
          parseFloat(max).toFixed(2);
      }
    };

    const getSelectedRangeEnvelope = () => {
      const selected =
        priceRanges
          .filter((checkbox) => checkbox.checked)
          .map(getRangeValues)
          .filter(Boolean);

      if (!selected.length) {
        return null;
      }

      return {
        min: Math.min(
          ...selected.map((range) => range.min)
        ),
        max: Math.max(
          ...selected.map((range) => range.max)
        )
      };
    };

    /*
     * Initialize the slider to the active metafield
     * buckets returned by Shopify. If none are active,
     * show the full available price range.
     */
    const selectedEnvelope =
      getSelectedRangeEnvelope();

    const startMin =
      selectedEnvelope
        ? selectedEnvelope.min
        : 0;

    const startMax =
      selectedEnvelope
        ? selectedEnvelope.max
        : maximum;

    if (
      slider.classList.contains(
        'noUi-target'
      ) &&
      slider.noUiSlider
    ) {
      slider.noUiSlider.destroy();
    }

    noUiSlider.create(
      slider,
      {
        start: [
          startMin,
          startMax
        ],
        connect: true,
        step: 0.01,
        direction: document.dir,
        handleAttributes: [
          {
            'aria-label': 'lower'
          },
          {
            'aria-label': 'upper'
          }
        ],
        range: {
          min: 0,
          max: maximum
        }
      }
    );

    updateEndpointLabels(
      startMin,
      startMax
    );

    updateNativePriceInputs(
      startMin,
      startMax
    );

    /*
     * CHECKBOX -> SLIDER
     *
     * The checkbox remains the real Shopify filter.
     * We only move the slider to visually cover the
     * selected bucket(s).
     */
    const syncSliderFromCheckboxes = () => {
      const envelope =
        getSelectedRangeEnvelope();

      const nextMin =
        envelope
          ? envelope.min
          : 0;

      const nextMax =
        envelope
          ? envelope.max
          : maximum;

      slider.noUiSlider.set([
        nextMin,
        nextMax
      ]);

      updateEndpointLabels(
        nextMin,
        nextMax
      );

      updateNativePriceInputs(
        nextMin,
        nextMax
      );
    };

    priceRanges.forEach((checkbox) => {
      checkbox.addEventListener(
        'input',
        syncSliderFromCheckboxes
      );
    });

    /*
     * SLIDER -> CHECKBOXES
     *
     * Select every metafield bucket that overlaps the
     * slider span. Returning the slider to its complete
     * range means "no price filter", so all buckets are
     * cleared instead of selecting every bucket.
     */
    const syncCheckboxesFromSlider = (values) => {
      if (!priceRanges.length) {
        return false;
      }

      const sliderMin =
        parseFloat(values[0]);

      const sliderMax =
        parseFloat(values[1]);

      if (
        Number.isNaN(sliderMin) ||
        Number.isNaN(sliderMax)
      ) {
        return false;
      }

      const isFullRange =
        sliderMin <= 0.01 &&
        sliderMax >= maximum - 0.01;

      let changed = false;

      priceRanges.forEach((checkbox) => {
        if (checkbox.disabled) {
          return;
        }

        const range =
          getRangeValues(checkbox);

        if (!range) {
          return;
        }

        const shouldCheck =
          !isFullRange &&
          range.max >= sliderMin &&
          range.min <= sliderMax;

        if (checkbox.checked !== shouldCheck) {
          checkbox.checked = shouldCheck;
          changed = true;
        }
      });

      return changed;
    };

    slider.noUiSlider.on(
      'update',
      (values) => {
        updateEndpointLabels(
          values[0],
          values[1]
        );

        updateNativePriceInputs(
          values[0],
          values[1]
        );
      }
    );

    slider.noUiSlider.on(
      'change',
      (values) => {
        const changed =
          syncCheckboxesFromSlider(
            values
          );

        /*
         * Submit exactly once after the user finishes
         * moving the slider.
         *
         * Dispatch the input event from one of the real
         * Price Range metafield checkboxes. This makes
         * the normal facet handler treat the slider
         * change exactly like a checkbox selection, so
         * Shopify refreshes products AND active facets.
         */
        if (changed && priceRanges.length) {
          priceRanges[0].dispatchEvent(
            new Event(
              'input',
              {
                bubbles: true
              }
            )
          );
        }
      }
    );
  }
}

customElements.define(
  'price-slider',
  PriceSlider
);